import { useState } from "react";
import { cn } from "@/lib/cn";
import { ManagerDrilldownPanel } from "./ManagerDrilldownPanel";
import type { SupervisorManagerRow, SupervisorStatusBadge } from "@/api/types";

/**
 * S2 Block 3 — Manager table (mockup v2). Counters + statusBadge are backend-owned
 * (AC-SH-MGR-002); row click → Manager Drilldown (managerId+team-scope, MGR-003).
 * lastActivity = CRM events (NOT presence, MGR-007). No online/presence (008).
 */

const BADGE: Record<SupervisorStatusBadge, { label: string; cls: string }> = {
  "High-risk": { label: "Високий ризик", cls: "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]" },
  "Needs-attention": { label: "Потребує уваги", cls: "bg-[hsl(var(--warning)/0.6)] text-[hsl(var(--warning-foreground))]" },
  "No-activity": { label: "Немає активного портфеля", cls: "bg-muted text-muted-foreground" },
  OK: { label: "ОК", cls: "bg-[hsl(var(--success)/0.7)] text-[hsl(var(--success-foreground))]" },
};

function initials(name: string | null): string {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "—";
}

function activityLabel(iso: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso.replace(" ", "T")).getTime();
  if (Number.isNaN(ts)) return "—";
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days <= 0) return "сьогодні";
  if (days === 1) return "вчора";
  return `${days} дн. тому`;
}

export function SupervisorManagerTable({ rows }: { rows: SupervisorManagerRow[] }) {
  const [selected, setSelected] = useState<SupervisorManagerRow | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border bg-card py-10 text-center text-sm text-muted-foreground">
        У команді немає менеджерів.
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-2xl border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <Th className="text-left">Менеджер</Th>
              <Th>Активні</Th>
              <Th>Задачі</Th>
              <Th>Прострочені</Th>
              <Th>Зустр. без звіту</Th>
              <Th>Звіти керівником</Th>
              <Th>Без next-action</Th>
              <Th>AR-risk</Th>
              <Th>Активність</Th>
              <Th className="text-left">Статус</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const badge = BADGE[r.statusBadge];
              const zero = r.statusBadge === "No-activity";
              return (
                <tr
                  key={r.managerId}
                  className="cursor-pointer border-b last:border-b-0 hover:bg-muted/60"
                  onClick={() => setSelected(r)}
                >
                  <Td className="text-left">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                        {initials(r.managerName)}
                      </span>
                      <span className="font-medium">{r.managerName ?? "—"}</span>
                    </div>
                  </Td>
                  <Num v={zero ? null : r.activeClients} />
                  <Num v={zero ? null : r.openTasks} />
                  <Num v={zero ? null : r.overdueTasks} bad={r.overdueTasks > 0} />
                  <Num v={zero ? null : r.meetingsNoReport} bad={r.meetingsNoReport > 0} />
                  {/* D-151 S2 — «заповнено керівником» (Q-I3=A): не «bad», але видимий */}
                  <Num v={zero ? null : (r.onBehalfSubmitted ?? 0)} />
                  <Num v={zero ? null : r.clientsNoAction} />
                  <Num v={zero ? null : r.arRiskClients} bad={(r.arRiskClients ?? 0) > 0} />
                  <Td className="text-muted-foreground">{activityLabel(r.lastActivityAt)}</Td>
                  <Td className="text-left">
                    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold", badge.cls)}>
                      ● {badge.label}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <ManagerDrilldownPanel
          managerId={selected.managerId}
          managerName={selected.managerName}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn("px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", className)}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-3 text-right", className)}>{children}</td>;
}

function Num({ v, bad }: { v: number | null; bad?: boolean }) {
  return (
    <Td className={cn("tabular-nums", v === null && "text-muted-foreground", bad && "font-bold text-[hsl(var(--destructive))]")}>
      {v === null ? "—" : v}
    </Td>
  );
}
